//-- Copyright (c) 2021 TMT Insights Group, LLC
import theme from '@/__configs/pbs/configs/theme';
import { darkBaseTheme, globalBaseTheme, colors as globalColors, lightBaseTheme } from '@/constants/baseThemes';
import { Palette, Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import polaris from '../../../assets/images/polaris_logo_color.png';
import logo from './assets/focus.svg';

// https://mui.com/customization/theming/

export type FocusTheme = Theme & {
  palette: Palette & {
    neutral: Record<string, string>;
  };
  customOverrides: Record<string, any>;
};

const GREY_10_COLOR = '#edf1ff';
const GREY_20_COLOR = '#bbbfd1';
const GREY_30_COLOR = '#a3a8ba';
const GREY_40_COLOR = '#7d818f';
const GREY_50_COLOR = '#4c4e5c';
const GREY_60_COLOR = '#3a3c4d';
const GREY_70_COLOR = '#303240';
const GREY_80_COLOR = '#262834';
const GREY_90_COLOR = '#1b1d27';
const GREY_100_COLOR = '#13141b';
export const FOCUS_GRADIENT_1 = `linear-gradient(
  284deg,
  rgba(40, 46, 61, 0.6) 10.87%,
  rgba(92, 97, 154, 0.33) 17.07%,
  rgba(144, 147, 248, 0.17) 25.93%,
  rgba(68, 211, 227, 0.24) 38.78%,
  rgba(82, 200, 231, 0.34) 44.1%,
  rgba(118, 170, 241, 0.6) 65.81%,
  rgba(170, 125, 255, 0.6) 83.97%,
  rgba(68, 211, 227, 0.6) 99.04%
)`;

export const colors = {
  light: deepmerge(globalColors.light, {
    primary: {
      main: '#76AAF1',
      dark: '#648ECA',
      light: '#76AAF1'
    },
    secondary: {
      main: '#ff7701',
      light: '#F0B61F',
      dark: '#cf6400'
    },
    tertiary: {
      main: '#00a197',
      light: '#14ccc536',
      dark: '#00a197'
    },
    success: {
      main: '#66eb92',
      dark: '#006e18',
      light: '#6ec782'
    },
    warning: {
      main: '#ff7d03'
    },
    pending: {
      main: '#ff5d00'
    },
    error: {
      main: '#EC3959',
      dark: '#D41E40',
      light: '#EC506C'
    },
    background: {
      default: '#eff2f5',
      paper: '#ffffff'
    },
    action: {
      hover: 'rgba(0,0,0,.1)',
      focus: 'rgba(0,0,0,.04)'
    },
    neutral: {
      0: '#000000',
      10: GREY_100_COLOR,
      20: GREY_90_COLOR,
      30: GREY_80_COLOR,
      40: GREY_70_COLOR,
      50: GREY_60_COLOR,
      60: GREY_50_COLOR,
      70: GREY_40_COLOR,
      80: GREY_30_COLOR,
      90: GREY_20_COLOR,
      100: GREY_10_COLOR
    }
  }),
  dark: deepmerge(globalColors.dark, {
    primary: {
      main: '#76AAF1',
      dark: '#648ECA',
      light: '#76AAF1',
      contrastText: '#000000'
    },
    secondary: {
      main: '#44D3E3',
      light: '#44D3E3',
      dark: '#44D3E3',
      contrastText: '#000000'
    },
    tertiary: {
      main: '#AA7DFF',
      light: '#D1B9FF',
      dark: '#AA7DFF',
      contrastText: '#272C32'
    },
    success: {
      main: '#66eb92',
      dark: '#189915',
      light: '#5CCD5A'
    },
    warning: {
      main: '#FFA100',
      dark: '#E59100',
      light: '#FFBD4D'
    },
    pending: {
      main: '#FF7D03',
      dark: '#E57103',
      light: '#FF9736'
    },
    error: {
      main: '#EC3959',
      dark: '#D41E40',
      light: '#EC506C'
    },
    background: {
      default: '#14171A',
      paper: '#262834'
    },
    action: {
      hover: '#263141',
      focus: 'rgb(118 170 241 / 42%)'
    },
    teal: {
      main: '#62BDBC',
      dark: '#24A3A1',
      light: '#ABCBCA'
    },
    neutral: {
      0: '#ffffff',
      10: GREY_10_COLOR,
      20: GREY_20_COLOR,
      30: GREY_30_COLOR,
      40: GREY_40_COLOR,
      50: GREY_50_COLOR,
      60: GREY_60_COLOR,
      70: GREY_70_COLOR,
      80: GREY_80_COLOR,
      90: GREY_90_COLOR,
      100: GREY_100_COLOR
    }
  })
};

export const POPPER_ZINDEX = 9999;
const BORDER_RADIUS = 5;

const globalTheme = deepmerge(globalBaseTheme, {
  shape: { borderRadius: BORDER_RADIUS },
  customOverrides: {
    Header: {
      background: 'linear-gradient(45deg, #0b0c0e, #000)',
      '& h1 > img': {
        height: 35
      }
    },
    Footer: {
      background: 'none'
    },
    TableStatus: {
      fontWeight: 800
    },
    TitleSearchTitleType: {
      '& .MuiChip-root': {
        width: 'auto !important'
      }
    },
    VersionCard: {
      backgroundColor: '#2451883f',
      '& > button': {
        fontSize: '1rem'
      }
    },
    StatusCard: {
      '& $icon': {
        borderRadius: 8,
        padding: 10,
        top: 10,
        right: 10,
        '& > svg': {
          fontSize: '1.4rem'
        }
      },
      label: {
        fontSize: '1.1rem'
      }
    },
    JobReviewHeading: {
      '& .MuiTypography-caption': {
        color: 'white',
        opacity: 0.8
      }
    },
    JobTitleCard: {
      '& .MuiChip-outlinedPrimary': {
        display: 'none'
      }
    },
    TypeLabel: {
      root: {
        fontSize: '.8rem'
      },
      series: {
        backgroundColor: '#8855ee !important'
      },
      episode: {
        backgroundColor: '#00c7bc !important'
      },
      feature: {
        backgroundColor: '#2e8ee9 !important'
      },
      trailer: {
        backgroundColor: '#ff8600 !important'
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 5
        }
      }
    }
  },
  components: {}
});

const TableNoResultsBaseStyles = {
  borderBottomLeftRadius: `${BORDER_RADIUS * 3.2}px`,
  borderBottomRightRadius: `${BORDER_RADIUS * 3.2}px`
};

const light = {
  palette: {
    mode: 'light',
    ...colors.light
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    DashboardWidget: {
      background: 'linear-gradient(212deg, #FFF 6.88%, rgba(255, 255, 255, 0.46) 83.4%)'
    },
    Calendar: {
      backgroundDefault: GREY_10_COLOR,
      background: GREY_20_COLOR,
      backgroundAlt: GREY_30_COLOR,
      backgroundStrong: GREY_90_COLOR,
      text: GREY_100_COLOR,
      textPrimary: GREY_90_COLOR,
      textSecondary: GREY_60_COLOR,
      textTertiary: GREY_50_COLOR,
      highlight: GREY_80_COLOR,
      highlightPrimary: GREY_10_COLOR,
      hover: GREY_50_COLOR,
      border: `1px solid ${GREY_10_COLOR}`
    },
    TitleBar: {
      background: '#ffffffc2'
    },
    Footer: {
      background: '#ffffffc2'
    },
    TypeLabel: {
      root: {
        boxShadow: 'none !important'
      }
    },
    ExpandRow: {
      backgroundColor: '#fff',
      '&.MuiTableRow-root:hover': {
        backgroundColor: '#fff'
      }
    },
    InventoryFile: {
      border: `1px solid #d7d7d7`,
      backgroundColor: '#faf9f9'
    },
    TitleArea: {
      border: `1px solid #d7d7d7`,
      boxShadow: '0px 10px 13px -6px rgb(0 0 0 / 4%), 0px 20px 31px 3px rgb(0 0 0 / 3%), 0px 8px 38px 7px rgb(0 0 0 / 2%)'
    },
    TableNoResults: {
      ...TableNoResultsBaseStyles,
      backgroundColor: colors.light.background.paper
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.base-Popper-root > div': {
          backgroundColor: colors.light.background.default + ' !important',
          color: theme.lightTheme.palette.text.primary + ' !important'
        },
        '.base-Popper-root': {
          zIndex: `${POPPER_ZINDEX}`
        },
        '.title-search-drawer-dialog .MuiDialog-paper': {
          width: '800px',
          maxWidth: '1000px'
        },
        '.scheduling-created-dialog .MuiDialog-paper': {
          width: '1000px',
          maxWidth: '1200px',
          minHeight: '400px'
        },
        '#mainNavpolaris > .MuiGrid-root': {
          width: '120px',
          background: `url(${polaris}) no-repeat center`,
          backgroundSize: 'contain',
          height: '50px'
        },
        ':root': {
          '--primary-main': `${colors.light.primary.main} !important`,
          '--primary-light': `${colors.light.primary.light} !important`,
          '--primary-dark': `${colors.light.primary.dark} !important`,
          '--secondary-main': colors.light.secondary.main,
          '--secondary-light': colors.light.secondary.light,
          '--secondary-dark': colors.light.secondary.dark,
          '--tertiary-main': colors.light.tertiary.main,
          '--tertiary-light': colors.light.tertiary.light,
          '--tertiary-dark': colors.light.tertiary.dark,
          '--default-main': colors.light.default.main,
          '--default-light': colors.light.default.light,
          '--default-dark': colors.light.default.dark,
          '--warning-main': colors.light.warning.main,
          '--warning-light': colors.light.warning.light,
          '--warning-dark': colors.light.warning.dark,
          '--success-main': colors.light.success.main,
          '--success-light': colors.light.success.light,
          '--success-dark': colors.light.success.dark,
          '--error-main': colors.light.error.main,
          '--error-light': colors.light.error.light,
          '--error-dark': colors.light.error.dark,
          '--teal-main': colors.light.teal.main,
          '--teal-light': colors.light.teal.light,
          '--teal-dark': colors.light.teal.dark
        }
      }
    }
  }
};

const dark = {
  palette: {
    mode: 'dark',
    ...colors.dark
  },
  customOverrides: {
    ...globalTheme.customOverrides,
    AddedJobAccordion: {
      backgroundColor: '#263141'
    },
    ButtonBar: {
      backgroundColor: 'rgba(9, 45, 90, 0.96)'
    },
    Calendar: {
      backgroundDefault: GREY_90_COLOR,
      background: GREY_70_COLOR,
      backgroundAlt: GREY_80_COLOR,
      backgroundStrong: GREY_10_COLOR,
      text: '#ffffff',
      textPrimary: GREY_10_COLOR,
      textSecondary: GREY_30_COLOR,
      textTertiary: GREY_40_COLOR,
      highlight: GREY_20_COLOR,
      highlightPrimary: GREY_100_COLOR,
      hover: GREY_50_COLOR,
      border: `1px solid ${GREY_100_COLOR}`
    },
    DashboardWidget: {
      background: 'linear-gradient(212deg, #353C50 9.41%, rgba(40, 45, 59, 0.52) 38.94%)'
    },
    TitleBar: {
      background: '#1A1D25',
      borderTop: '1px solid #111317'
    },
    InventoryFile: {
      backgroundColor: '#040404'
    },
    TitleSearch: {
      '& .MuiInputBase-root': {
        background: '#090909'
      }
    },
    TypeLabel: {
      root: {
        color: '#1E1E1E'
      }
    },
    TableNoResults: {
      ...TableNoResultsBaseStyles,
      backgroundColor: colors.dark.background.paper
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#13141B',
          backgroundImage: FOCUS_GRADIENT_1
        },
        '.filters-containter .MuiChip-colorPrimary.MuiChip-filled': { color: 'black !important' },
        '.filters-containter .MuiChip-colorPrimary.MuiChip-default': { color: 'black !important' },
        '.filters-containter .MuiChip-colorDefault.MuiChip-outlined': { borderColor: 'white !important' },
        '.MuiDialog-container > .MuiDialog-paper': { backgroundColor: colors.dark.background.default },
        '.title-search-drawer-dialog .MuiDialog-paper': {
          width: '800px',
          maxWidth: '1000px'
        },
        '.scheduling-created-dialog .MuiDialog-paper': {
          width: '1000px',
          maxWidth: '1200px',
          minHeight: '400px'
        },
        '.MuiTableRow-root:hover .expandable.MuiTableCell-body, .MuiTableRow-root .expandable.MuiTableCell-body': {
          background: 'none!important'
        },
        '#mainNavpolaris > .MuiGrid-root': {
          width: '120px',
          background: `url(${polaris}) no-repeat center`,
          backgroundSize: 'contain',
          height: '50px'
        },
        '.base-Popper-root > div': {
          backgroundColor: colors.dark.background.default + ' !important'
        },
        '.base-Popper-root': {
          zIndex: `${POPPER_ZINDEX}`
        },
        ':root': {
          '--primary-main': `${colors.dark.primary.main} !important`,
          '--primary-light': `${colors.dark.primary.light} !important`,
          '--primary-dark': `${colors.dark.primary.dark} !important`,
          '--secondary-main': colors.dark.secondary.main,
          '--secondary-light': colors.dark.secondary.light,
          '--secondary-dark': colors.dark.secondary.dark,
          '--tertiary-main': colors.dark.tertiary.main,
          '--tertiary-light': colors.dark.tertiary.light,
          '--tertiary-dark': colors.dark.tertiary.dark,
          '--default-main': colors.dark.default.main,
          '--default-light': colors.dark.default.light,
          '--default-dark': colors.dark.default.dark,
          '--warning-main': colors.dark.warning.main,
          '--warning-light': colors.dark.warning.light,
          '--warning-dark': colors.dark.warning.dark,
          '--success-main': colors.dark.success.main,
          '--success-light': colors.dark.success.light,
          '--success-dark': colors.dark.success.dark,
          '--error-main': colors.dark.error.main,
          '--error-light': colors.dark.error.light,
          '--error-dark': colors.dark.error.dark,
          '--teal-main': colors.dark.teal.main,
          '--teal-light': colors.dark.teal.light,
          '--teal-dark': colors.dark.teal.dark
        },
        '.MuiPopover-paper': {
          backgroundColor: colors.dark.background.default
        },
        '.react-datepicker': {
          backgroundColor: colors.dark.background.default + ' !important'
        }
      }
    }
  }
};

const lightMergedWithGlobal = deepmerge(light, globalTheme);
const darkMergedWithGlobal = deepmerge(dark, globalTheme);

const lightTheme = createTheme(deepmerge(lightBaseTheme, lightMergedWithGlobal));
const darkTheme = createTheme(deepmerge(darkBaseTheme, darkMergedWithGlobal));

export default { darkTheme, lightTheme, logo };
